import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Palette,
  LayoutGrid,
  FileText,
  BookOpen,
  Settings,
  PanelLeft,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { SwatchBookIcon } from "@/components/icons/SwatchBookIcon";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { STRONOM_COLLAPSIBLE_RAIL } from "@/features/footwear-studio/featureFlags";
import { StudioRailUploadButton } from "./StudioRailUpload";

const RAIL_EXPANDED_STORAGE_KEY = "stronom_rail_expanded";

const SIDEBAR_ITEMS = [
  {
    label: "Footwear Studio",
    to: "/footwear-studio",
    icon: Palette,
    disabled: false,
  },
  {
    label: "Collection Plan",
    to: "/footwear-studio/collection-plan",
    icon: LayoutGrid,
    disabled: false,
  },
  {
    label: "Artwork",
    to: "/footwear-studio/artwork",
    icon: FileText,
    disabled: false,
  },
  {
    label: "Materials",
    to: "/footwear-studio/materials",
    icon: SwatchBookIcon,
    disabled: false,
  },
  {
    label: "Rule Library",
    to: "/footwear-studio/rules",
    icon: BookOpen,
    disabled: false,
  },
];

const BOTTOM_ITEMS = [
  {
    label: "Settings",
    to: "/footwear-studio/settings",
    icon: Settings,
    disabled: true,
  },
];

function SidebarNavItem({
  item,
  isActive,
  expanded = false,
}: {
  item: (typeof SIDEBAR_ITEMS)[number];
  isActive: boolean;
  expanded?: boolean;
}) {
  const content = (
    <div
      className={cn(
        "relative flex h-14 items-center transition-colors duration-[160ms]",
        expanded ? "w-full gap-3 px-4" : "w-14 justify-center",
        item.disabled && "cursor-not-allowed",
        !item.disabled && "cursor-pointer",
        isActive && "text-sky-400 hover:bg-sky-400/[0.08]",
        !isActive &&
          !item.disabled &&
          "text-[#b8bec8] hover:bg-white/[0.06] hover:text-[#d4dae2]",
        !isActive && item.disabled && "text-neutral-700"
      )}
    >
      {isActive ? (
        <span
          aria-hidden
          className="absolute left-0 top-1/2 h-10 w-[2.5px] -translate-y-1/2 rounded bg-sky-400"
        />
      ) : null}
      <item.icon className="size-5 shrink-0" />
      {expanded ? (
        <span className="truncate text-sm">{item.label}</span>
      ) : null}
    </div>
  );

  const tooltipLabel = item.disabled ? "Coming soon" : item.label;

  if (expanded) {
    if (item.disabled) {
      return <span className="block">{content}</span>;
    }
    return (
      <Link to={item.to} className="block">
        {content}
      </Link>
    );
  }

  if (item.disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="block">{content}</span>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={8} className="border-neutral-700 bg-bgSurface">
          {tooltipLabel}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link to={item.to} className="block">
          {content}
        </Link>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={8} className="border-neutral-700 bg-bgSurface">
        {tooltipLabel}
      </TooltipContent>
    </Tooltip>
  );
}

function AccountMenu({ expanded = false }: { expanded?: boolean }) {
  const { user, orgKey, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    const result = await signOut();
    if (!result.error) {
      navigate("/login", { replace: true });
    }
  };

  const trigger = (
    <button
      type="button"
      className={cn(
        "flex h-14 items-center text-[#b8bec8] hover:text-[#d4dae2] transition-colors outline-none",
        expanded ? "w-full gap-3 px-4" : "w-14 justify-center"
      )}
      aria-label="Account menu"
    >
      <Avatar className="size-8 shrink-0">
        <AvatarFallback className="bg-neutral-700 text-neutral-300 text-xs font-medium">
          AS
        </AvatarFallback>
      </Avatar>
      {expanded ? <span className="truncate text-sm">Account</span> : null}
    </button>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        side="right"
        sideOffset={8}
        align="end"
        className="min-w-[220px] border-neutral-700 bg-bgSurface text-neutral-200"
      >
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm text-neutral-200 truncate">
              {user?.email ?? "Signed in"}
            </span>
            {orgKey ? (
              <span className="text-xs text-neutral-500 truncate">{orgKey}</span>
            ) : null}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-neutral-800" />
        {/* Placeholder — no account page exists yet */}
        <DropdownMenuItem
          disabled
          className="text-neutral-500 focus:bg-neutral-800 focus:text-neutral-500"
        >
          Account
          <span className="ml-auto text-[10px] text-neutral-600">Soon</span>
        </DropdownMenuItem>
        {/* Placeholder — no billing page exists */}
        <DropdownMenuItem
          disabled
          className="text-neutral-500 focus:bg-neutral-800 focus:text-neutral-500"
        >
          Membership & billing
          <span className="ml-auto text-[10px] text-neutral-600">Soon</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-neutral-800" />
        <DropdownMenuItem
          onSelect={() => {
            void handleSignOut();
          }}
          className="cursor-pointer focus:bg-neutral-800 focus:text-neutral-100"
        >
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function RailToggle({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) {
  const button = (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "flex h-14 items-center text-[#b8bec8] transition-colors duration-[160ms]",
        "hover:bg-[var(--bg-raised)] hover:text-[#d4dae2]",
        expanded ? "w-full gap-3 px-4" : "w-14 justify-center"
      )}
      aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
      aria-expanded={expanded}
    >
      <PanelLeft className="size-5 shrink-0" />
      {expanded ? <span className="truncate text-sm">Collapse</span> : null}
    </button>
  );

  if (expanded) {
    return button;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={8} className="border-neutral-700 bg-bgSurface">
        Expand sidebar
      </TooltipContent>
    </Tooltip>
  );
}

function UploadRow({
  expanded,
  collapsible,
}: {
  expanded: boolean;
  collapsible: boolean;
}) {
  if (!collapsible) {
    return <StudioRailUploadButton />;
  }

  return (
    <span className="block">
      <div
        className={cn(
          "relative flex h-14 items-center transition-colors duration-[160ms]",
          expanded ? "w-full gap-3 px-4" : "w-14 justify-center",
        )}
      >
        <StudioRailUploadButton inlineIcon={expanded} showTooltip={!expanded} />
        {expanded ? (
          <span className="truncate text-sm text-[#b8bec8]">New design</span>
        ) : null}
      </div>
    </span>
  );
}

export function StudioSidebar() {
  const location = useLocation();
  const collapsible = STRONOM_COLLAPSIBLE_RAIL;
  const [railExpanded, setRailExpanded] = useState(false);

  useEffect(() => {
    if (!collapsible) return;
    try {
      setRailExpanded(localStorage.getItem(RAIL_EXPANDED_STORAGE_KEY) === "true");
    } catch {
      setRailExpanded(false);
    }
  }, [collapsible]);

  useEffect(() => {
    if (!collapsible) return;
    try {
      localStorage.setItem(RAIL_EXPANDED_STORAGE_KEY, String(railExpanded));
    } catch {
      // ignore storage failures
    }
  }, [collapsible, railExpanded]);

  const expanded = collapsible && railExpanded;

  const isActive = (to: string) => {
    if (to === "/footwear-studio") {
      return location.pathname === "/footwear-studio" || location.pathname === "/footwear-studio/";
    }
    return location.pathname === to || location.pathname.startsWith(to + "/");
  };

  const logoLink = (
    <Link
      to="/"
      className="flex h-14 w-14 items-center justify-center bg-transparent"
      aria-label="Back to STRONOM"
    >
      <img
        src="/stronom-mark.png"
        alt="STRONOM"
        className="h-9 w-9 object-contain"
      />
    </Link>
  );

  return (
    <TooltipProvider delayDuration={300}>
      <aside
        data-studio-sidebar=""
        className={cn(
          "fixed left-0 top-0 z-[110] h-screen flex flex-col",
          expanded ? "w-48 shadow-[4px_0_16px_rgba(0,0,0,0.35)]" : "w-14",
          collapsible && "transition-[width] duration-[180ms] ease-out",
          "bg-bgPage border-r border-neutral-800",
          "hidden md:flex"
        )}
      >
        {/* Top section — logo cell matches header h-14 (56px) for title alignment */}
        <div className="flex flex-col">
          {expanded ? (
            logoLink
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>{logoLink}</TooltipTrigger>
              <TooltipContent side="right" sideOffset={8} className="border-neutral-700 bg-bgSurface">
                Back to STRONOM
              </TooltipContent>
            </Tooltip>
          )}
          {collapsible ? (
            <RailToggle
              expanded={expanded}
              onToggle={() => setRailExpanded((value) => !value)}
            />
          ) : null}
          <UploadRow expanded={expanded} collapsible={collapsible} />
          {SIDEBAR_ITEMS.map((item) => (
            <SidebarNavItem
              key={item.to + item.label}
              item={item}
              isActive={isActive(item.to)}
              expanded={expanded}
            />
          ))}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Bottom section */}
        <div className="flex flex-col pb-4">
          {BOTTOM_ITEMS.map((item) => (
            <SidebarNavItem
              key={item.to}
              item={item}
              isActive={isActive(item.to)}
              expanded={expanded}
            />
          ))}
          <div className="my-1 mx-2 h-px bg-neutral-800" />
          <AccountMenu expanded={expanded} />
        </div>
      </aside>
    </TooltipProvider>
  );
}
